// Pure helpers for the Onboarding page
// (docs/architecture/onboarding-requests.md). No React, no Firestore, so the
// ordering and wording rules are unit-testable on their own.

import moment from 'moment';
import {
    LinkProviders,
    OnboardingRequestStatuses,
    OPEN_ONBOARDING_REQUEST_STATUSES,
    WITHDRAWABLE_ONBOARDING_REQUEST_STATUSES
} from '@embertracking/common';

// MUI Chip colours. `needs_info` is the one that asks something of the
// department, so it is the one that stands out.
export const STATUS_COLORS = {
    submitted: 'default',
    in_progress: 'info',
    needs_info: 'warning',
    completed: 'success',
    declined: 'error',
    withdrawn: 'default'
};

export const statusLabel = status => OnboardingRequestStatuses[status] || status || 'Unknown';

export const isOpen = request => OPEN_ONBOARDING_REQUEST_STATUSES.includes(request?.status);

export const canWithdraw = request => WITHDRAWABLE_ONBOARDING_REQUEST_STATUSES.includes(request?.status);

const millis = value => (value ? moment(value).valueOf() : 0);

// Requests waiting on the department first (they're the ones someone here has
// to act on), then everything else still open, then the closed ones; newest
// activity first within each band.
const band = request => (request.status === 'needs_info' ? 0 : isOpen(request) ? 1 : 2);

export const sortRequests = (requests = []) => [...requests].sort((a, b) =>
    band(a) - band(b) || millis(b.lastActivityAt || b.createdAt) - millis(a.lastActivityAt || a.createdAt) || String(a.id).localeCompare(String(b.id)));

// The operator reads as "Ember Tracking" to the department, whatever the
// account behind the operator session is called. The operator sees real names.
export const authorLabel = (row, {viewerIsOperator = false} = {}) => {
    if (row?.authorRole === 'operator' && !viewerIsOperator) return 'Ember Tracking';
    return row?.authorName || 'Someone';
};

// A request the operator filed for an admin reads as that admin's, with a note
// of who actually typed it in.
const enteredByLabel = (row, {viewerIsOperator = false} = {}) =>
    (viewerIsOperator ? row.enteredByName || 'Ember Tracking' : 'Ember Tracking');

export const describeActivity = (row, {viewerIsOperator = false} = {}) => {
    if (row.type === 'status') {
        if (row.automatic) return `Moved to ${statusLabel(row.to).toLowerCase()} after a reply`;
        if (row.to === 'withdrawn') return 'Withdrew this request';
        if (row.from && ['completed', 'declined', 'withdrawn'].includes(row.from)) return `Reopened as ${statusLabel(row.to).toLowerCase()}`;
        return `Marked ${statusLabel(row.to).toLowerCase()}`;
    }
    if (row.type === 'attachments') {
        const parts = [];
        if (row.fileNames?.length) parts.push(`${row.fileNames.length} file${row.fileNames.length === 1 ? '' : 's'}`);
        if (row.links?.length) parts.push(`${row.links.length} link${row.links.length === 1 ? '' : 's'}`);
        return `Added ${parts.join(' and ') || 'attachments'}`;
    }
    if (row.type === 'created') {
        return row.enteredBy ? `Sent this request (added by ${enteredByLabel(row, {viewerIsOperator})})` : 'Sent this request';
    }
    return '';
};

// The thread, newest first, ending with the request itself — the same order
// the task activity timeline uses.
export const buildTimeline = (request, activity = []) => {
    const created = request ? [{
        id: '__created',
        type: 'created',
        author: request.createdBy,
        authorName: request.createdByName,
        authorRole: request.createdByRole,
        enteredBy: request.enteredBy,
        enteredByName: request.enteredByName,
        createdAt: request.createdAt
    }] : [];
    return [...activity, ...created].sort((a, b) =>
        millis(b.createdAt) - millis(a.createdAt) || (a.type === 'created' ? 1 : b.type === 'created' ? -1 : String(b.id).localeCompare(String(a.id))));
};

// FileField's value → what the api binds. Everything else on a field item
// (preview urls, sizes) is UI-only, and the api re-reads the trusted copy from
// the upload's binding anyway.
export const toRequestFiles = (value = []) => (Array.isArray(value) ? value : [])
    .filter(item => item?.attachmentId && item?.filePath)
    .map(({attachmentId, filePath, thumbnailPath, name, mimeType}) => ({
        attachmentId,
        filePath,
        ...(thumbnailPath ? {thumbnailPath} : {}),
        ...(name ? {name} : {}),
        ...(mimeType ? {mimeType} : {})
    }));

export const providerLabel = provider => LinkProviders[provider] || LinkProviders.other;
